import { navigate } from '../../app/routes';
import { useAppStore } from '../../app/store';
import { FanChart } from '../../charts/FanChart';
import { StressComparisonChart } from '../../charts/StressComparisonChart';
import { TerminalWealthHistogram } from '../../charts/TerminalWealthHistogram';
import { EmptyState } from '../../components/EmptyState';
import { StatCard } from '../../components/StatCard';
import { formatCompactCurrency, formatPercent } from '../../design/format';
import { SuccessHeadline } from './SuccessHeadline';
import './results.css';

/**
 * RESULTS DASHBOARD (PLAN.md M7 screen 2). Reads the in-memory `ResultSet` (+ optional
 * `ScenarioComparison`) from the app store and lays out, with an orchestrated staggered reveal:
 *  - the SUCCESS-PROBABILITY headline (count-up + confidence ring);
 *  - stat cards (median terminal wealth, p5/p95 terminal, worst drawdown);
 *  - the FAN CHART (percentile bands per year);
 *  - the TERMINAL-WEALTH HISTOGRAM (with median marker);
 *  - the STRESS-SCENARIO COMPARISON (retire-into-YYYY + worst/best, baseline neutral).
 *
 * Before any run, an EmptyState offers a one-click client-side run (the store's `runActive` thunk,
 * which assembles synthetic illustrative ReturnData + sets `result`/`stress`); a re-run control
 * sits in the headline row. The MVP runs the engine IN THE BROWSER on synthetic returns, so the
 * headline labels itself "illustrative".
 */
export function ResultsScreen(): React.ReactElement {
  const result = useAppStore((s) => s.result);
  const stress = useAppStore((s) => s.stress);
  const plan = useAppStore((s) => s.plan);
  const lastRunSeed = useAppStore((s) => s.lastRunSeed);
  const runStatus = useAppStore((s) => s.runStatus);
  const runError = useAppStore((s) => s.runError);
  const runActive = useAppStore((s) => s.runActive);

  const running = runStatus === 'running';

  if (result === null) {
    return (
      <div className="results">
        <EmptyState
          title="No results yet"
          body="Run a simulation from your plan to see the success probability, the wealth fan chart, the terminal-wealth distribution, and stress scenarios. The MVP runs the engine in your browser on illustrative synthetic returns."
          actionLabel={running ? 'Running…' : 'Run simulation'}
          onAction={() => void runActive()}
        />
        {runError !== null && (
          <p className="results__error" style={{ marginTop: 'var(--sp-4)' }}>
            {runError}
          </p>
        )}
      </div>
    );
  }

  const summary = result.terminalWealthSummary;
  const paths = result.ledgers.length;
  // Worst drawdown across the baseline scenario, when a stress comparison is available.
  const worstDd = stress?.baseline.worstDrawdown;

  return (
    <div className="results">
      <SuccessHeadline probability={result.successProbability} paths={paths} illustrative />

      <div className="results__controls reveal reveal--1">
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void runActive()}
          disabled={running}
        >
          {running ? 'Running…' : 'Re-run simulation'}
        </button>
        <span className="results__note">
          {paths.toLocaleString('en-US')} paths · seed {lastRunSeed}
        </span>
        {runError !== null && <span className="results__error">{runError}</span>}
      </div>

      <div className="results__stats">
        <StatCard
          label="Median terminal wealth"
          value={summary ? formatCompactCurrency(summary.median) : '—'}
          emphasis="display"
        />
        <StatCard
          label="5th percentile"
          value={summary ? formatCompactCurrency(summary.atPercentiles[0] ?? 0) : '—'}
          tone="neg"
        />
        <StatCard
          label="95th percentile"
          value={summary ? formatCompactCurrency(summary.atPercentiles.at(-1) ?? 0) : '—'}
          tone="pos"
        />
        {worstDd ? (
          <StatCard
            label="Worst drawdown"
            value={formatPercent(worstDd.maxDrawdown)}
            tone="warn"
            note={`bottomed ${worstDd.troughYear}`}
          />
        ) : (
          <StatCard label="Success" value={formatPercent(result.successProbability)} />
        )}
      </div>

      <div className="card reveal reveal--2">
        <div className="card__title">Projected wealth — percentile fan</div>
        <FanChart bands={result.bands} startYear={plan.startYear} />
      </div>

      <div className="results__row">
        <div className="card reveal reveal--3">
          <div className="card__title">Terminal wealth distribution</div>
          <TerminalWealthHistogram terminalWealth={result.terminalWealth} summary={summary} />
        </div>
        <div className="card reveal reveal--4">
          <div className="card__title">Stress scenarios</div>
          {stress !== null ? (
            <StressComparisonChart comparison={stress} metric="success" />
          ) : (
            <p className="results__note">
              Run with stress scenarios to compare retire-into-year cohorts against the baseline.
            </p>
          )}
        </div>
      </div>

      <div style={{ marginTop: 'var(--sp-2)' }}>
        <button type="button" className="btn" onClick={() => navigate('ledger')}>
          Open ledger drill-down →
        </button>
      </div>
    </div>
  );
}
